import { useEffect, useState } from "react";
import { Loader2, Send, FileText, FileSignature, MessageCircle, ExternalLink, Images, Users } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg, apiUrl } from "@/lib/api";
import { STATUS, ROLE_LABELS, fileUrl } from "@/lib/activity";
import { tanggalPanjang } from "@/lib/format";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { CategoryBanner } from "@/components/activity/CategoryBanner";
import { ShareButtons } from "@/components/activity/ShareButtons";
import { StatusDialog } from "./StatusDialog";

const Section = ({ title, icon: Icon, children, testId }) => (
  <div className="rounded-2xl border border-emerald-900/10 p-5" data-testid={testId}>
    <h4 className="font-semibold text-emerald-950 flex items-center gap-2 mb-3">{Icon && <Icon size={16} />} {title}</h4>
    {children}
  </div>
);

export const ActivityManage = ({ activity, onClose, onChanged }) => {
  const [a, setA] = useState(activity);
  const [pending, setPending] = useState(null);
  const [busy, setBusy] = useState("");

  useEffect(() => { setA(activity); }, [activity]);
  if (!a) return null;

  const url = `${window.location.origin}/kegiatan/${a.id}`;
  const token = localStorage.getItem("access_token");
  const pdfUrl = (doc) => `${apiUrl(`/activities/${a.id}/pdf/${doc}`)}${token ? `?auth=${token}` : ""}`;
  const canPdf = ["terkonfirmasi", "selesai"].includes(a.status);

  const changeStatus = async (payload) => {
    try {
      const { data } = await api.patch(`/activities/${a.id}/status`, { ...payload, url });
      setA(data);
      setPending(null);
      onChanged?.();
      if (data.wa_sent === true) toast.success(`Status: ${STATUS[data.status].label}. Pesan WhatsApp terkirim ke grup.`);
      else if (data.wa_sent === false) toast.warning(`Status: ${STATUS[data.status].label}. WA grup tidak terkirim (token/ID grup belum diatur) — gunakan tombol kirim manual.`);
      else toast.success(`Status: ${STATUS[data.status].label}`);
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  const pick = (status) => {
    if (status === a.status) return;
    if (status === "terjadwal") return changeStatus({ status, confirmed: true });
    setPending(status);
  };

  const notify = async (kind) => {
    setBusy(kind);
    try {
      await api.post(`/activities/${a.id}/notify`, { kind, url });
      toast.success(kind === "group" ? "Pesan terkirim ke Grup WA" : "Pengingat terkirim ke pemateri");
      const { data } = await api.get(`/activities/${a.id}`);
      setA(data);
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy("");
    }
  };

  const speaker = Object.entries(a.people || {}).find(([r]) => ["penceramah", "mubaligh", "khatib", "imam"].includes(r));
  const fmtTs = (s) => (s ? s.slice(0, 16).replace("T", " ") : null);

  return (
    <Dialog open={!!activity} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-4xl max-h-[92vh] overflow-y-auto p-0" data-testid="activity-manage-dialog">
        <CategoryBanner category={a.category} status={a.status} image={a.banner_path} compact className="h-[160px] sm:h-[200px] rounded-t-lg" />
        <div className="p-6 space-y-5">
          <DialogHeader>
            <DialogTitle className="text-xl" data-testid="manage-title">{a.title}</DialogTitle>
            <p className="text-sm text-slate-500">{tanggalPanjang(a.date)} · {a.time || "-"}{a.time_end ? `–${a.time_end}` : ""} WIB · {a.location}
              {Object.entries(a.people || {}).map(([r, p]) => <span key={r}> · {ROLE_LABELS[r]}: <b>{p.name}</b></span>)}</p>
          </DialogHeader>

          <Section title="Status Kegiatan" testId="manage-status">
            <div className="flex flex-wrap gap-2">
              {Object.entries(STATUS).map(([k, v]) => (
                <button key={k} type="button" onClick={() => pick(k)} className={`rounded-full px-4 py-1.5 text-sm font-semibold border transition-colors ${a.status === k ? `${v.tone} border-transparent ring-2 ring-offset-1 ring-emerald-700` : "border-emerald-900/15 text-slate-600 hover:bg-emerald-50"}`} data-testid={`manage-status-${k}`}>{v.label}</button>
              ))}
            </div>
            <p className="text-xs text-slate-500 mt-3">Terjadwal: H-1 pukul 17.00 WIB sistem mengirim WA konfirmasi ke pemateri · Terkonfirmasi: info agenda ke Grup WA + PDF Berita Acara & Daftar Hadir · Selesai: laporan + ringkasan ke Grup WA · Dibatalkan: pemberitahuan ke Grup WA.</p>
            {a.status === "dibatalkan" && a.cancel_reason && <p className="mt-2 text-sm text-red-700" data-testid="manage-cancel-reason">Alasan pembatalan: {a.cancel_reason}</p>}
          </Section>

          <div className="grid md:grid-cols-2 gap-5">
            <Section title="Kirim WhatsApp (Manual)" icon={MessageCircle} testId="manage-notify">
              <div className="flex flex-wrap gap-2">
                <Button type="button" size="sm" variant="outline" className="rounded-full" disabled={!!busy || !speaker} onClick={() => notify("speaker")} data-testid="manage-remind-speaker-btn">{busy === "speaker" ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} className="mr-1" />} Ingatkan Pemateri</Button>
                <Button type="button" size="sm" variant="outline" className="rounded-full" disabled={!!busy} onClick={() => notify("group")} data-testid="manage-share-group-btn">{busy === "group" ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} className="mr-1" />} Kirim ke Grup WA</Button>
              </div>
              <ul className="mt-3 text-xs text-slate-500 space-y-1" data-testid="manage-notify-log">
                <li>Pengingat pemateri: {fmtTs(a.notified?.speaker_h1) ? `${fmtTs(a.notified.speaker_h1)} ${a.notified.speaker_ok ? "✓" : "(gagal)"}` : "belum terkirim"}</li>
                {["terkonfirmasi", "selesai", "dibatalkan"].map((s) => a.notified?.[`group_${s}`] && <li key={s}>Grup WA ({STATUS[s].label}): {fmtTs(a.notified[`group_${s}`])} {a.notified[`group_${s}_ok`] ? "✓" : "(gagal)"}</li>)}
              </ul>
              <p className="text-xs text-slate-400 mt-2">Pesan menyesuaikan status saat ini. Isi preset: {speaker ? `${ROLE_LABELS[speaker[0]]} ${speaker[1].name}` : "pemateri belum ditentukan"}.</p>
              <div className="mt-3 pt-3 border-t border-emerald-900/10"><ShareButtons activity={a} /></div>
              <a href={url} target="_blank" rel="noreferrer" className="mt-2 inline-flex items-center gap-1 text-xs text-emerald-800 underline" data-testid="manage-public-link"><ExternalLink size={12} /> Buka halaman publik</a>
            </Section>

            <Section title="Dokumen PDF" icon={FileText} testId="manage-pdf">
              {canPdf ? (
                <div className="flex flex-col gap-2">
                  <a href={pdfUrl("berita-acara")} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 rounded-2xl border border-emerald-900/15 px-4 py-3 text-sm font-medium hover:bg-emerald-50" data-testid="pdf-berita-acara-btn"><FileSignature size={16} className="text-emerald-700" /> Berita Acara Kegiatan (PDF)</a>
                  <a href={pdfUrl("daftar-hadir")} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 rounded-2xl border border-emerald-900/15 px-4 py-3 text-sm font-medium hover:bg-emerald-50" data-testid="pdf-daftar-hadir-btn"><Users size={16} className="text-emerald-700" /> Daftar Hadir Jamaah (PDF, siap cetak)</a>
                  <p className="text-xs text-slate-400">Kop surat memakai logo, nama, dan alamat dari Profil Masjid.</p>
                </div>
              ) : (
                <p className="text-sm text-slate-500" data-testid="pdf-locked">Dokumen Berita Acara dan Daftar Hadir akan tersedia setelah status <b>Terkonfirmasi</b>.</p>
              )}
            </Section>
          </div>

          {a.report && (
            <Section title="Laporan Pasca Kegiatan" icon={Images} testId="manage-report">
              <p className="text-sm"><b>{a.report.attendance_count || 0}</b> jamaah hadir</p>
              {a.report.summary && <p className="text-sm text-slate-700 mt-2 whitespace-pre-line">{a.report.summary}</p>}
              {a.report.notes && <p className="text-xs text-slate-500 mt-2">Catatan: {a.report.notes}</p>}
              {a.report.photos?.length > 0 && <div className="mt-3 flex flex-wrap gap-2">{a.report.photos.map((p) => <img key={p} src={fileUrl(p)} alt="" className="h-20 w-20 rounded-xl object-cover" />)}</div>}
              <Button type="button" size="sm" variant="outline" className="rounded-full mt-3" onClick={() => setPending("selesai")} data-testid="report-edit-btn">Ubah Laporan</Button>
            </Section>
          )}
        </div>
      </DialogContent>
      {pending && <StatusDialog status={pending} activity={a} onClose={() => setPending(null)} onSubmit={changeStatus} />}
    </Dialog>
  );
};
